import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Slider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import api from '../../services/api';
import toast from 'react-hot-toast';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const WorkingWithManager = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [workingWith, setWorkingWith] = useState([]);
  const [filteredWorkingWith, setFilteredWorkingWith] = useState([]);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [currentType, setCurrentType] = useState('category');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    display_order: 0,
    category_id: '',
    is_active: 1
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    fetchCategories();
    fetchWorkingWith();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/working-with/categories');
      setCategories(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const fetchWorkingWith = async (categoryId = null) => {
    try {
      const url = categoryId ? `/working-with/working-with?category_id=${categoryId}` : '/working-with/working-with';
      const response = await api.get(url);
      setWorkingWith(response.data.data);
      if (categoryId) {
        setFilteredWorkingWith(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch working with');
    }
  };

  const handleOpenDialog = (type, item = null) => {
    setCurrentType(type);
    setEditingItem(item);
    
    if (item) {
      setFormData({
        name: item.name || '',
        description: item.description || '',
        display_order: item.display_order || 0,
        category_id: item.category_id || '',
        is_active: item.is_active === 1
      });
    } else {
      setFormData({
        name: '',
        description: '',
        display_order: 0,
        category_id: '',
        is_active: 1
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }

    if (currentType === 'working' && !formData.category_id) {
      toast.error('Please select a category');
      return;
    }

    setLoading(true);
    try {
      let response;
      let url;
      
      if (currentType === 'category') {
        url = '/working-with/categories';
      } else {
        url = '/working-with/working-with';
      }
      
      if (editingItem) {
        response = await api.put(`${url}/${editingItem.id}`, formData);
      } else {
        response = await api.post(url, formData);
      }
      
      if (response.data.success) {
        toast.success(`${currentType === 'category' ? 'Category' : 'Working With'} ${editingItem ? 'updated' : 'added'} successfully`);
        setDialogOpen(false);
        
        // Reset form
        setFormData({
          name: '',
          description: '',
          display_order: 0,
          category_id: '',
          is_active: 1
        });
        
        // Refresh data
        if (currentType === 'category') {
          fetchCategories();
        } else {
          fetchWorkingWith();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to save`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      try {
        let url;
        if (type === 'category') {
          url = `/working-with/categories/${id}`;
        } else {
          url = `/working-with/working-with/${id}`;
        }
        
        const response = await api.delete(url);
        if (response.data.success) {
          toast.success(`${type === 'category' ? 'Category' : 'Working With'} deleted successfully`);
          
          if (type === 'category') {
            fetchCategories();
          } else {
            fetchWorkingWith();
          }
        }
      } catch (error) {
        toast.error(error.response?.data?.message || `Failed to delete`);
      }
    }
  };

  const handleCategoryFilter = async (categoryId) => {
    setCategoryFilter(categoryId);
    if (categoryId === 'all') {
      await fetchWorkingWith();
    } else {
      await fetchWorkingWith(categoryId);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id == categoryId);
    return category ? category.name : 'N/A';
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        Working With Management
      </Typography>

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab icon={<CategoryIcon />} label="Categories" />
          <Tab icon={<BusinessIcon />} label="Working With" />
        </Tabs>

        {/* Categories Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <TextField
              size="small"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ width: 300 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('category')}
              sx={{
                background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
              }}
            >
              Add Category
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Display Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories
                  .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((category) => (
                    <TableRow key={category.id} hover>
                      <TableCell>
                        <Typography fontWeight="500">{category.name}</Typography>
                      </TableCell>
                      <TableCell>{category.description || '-'}</TableCell>
                      <TableCell>{category.display_order}</TableCell>
                      <TableCell>
                        <Chip 
                          label={category.is_active ? 'Active' : 'Inactive'}
                          color={category.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenDialog('category', category)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete('category', category.id, category.name)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {categories.length === 0 && !loading && (
            <Box textAlign="center" py={5}>
              <Typography color="textSecondary">No categories found. Click "Add Category" to create one.</Typography>
            </Box>
          )}
        </TabPanel>

        {/* Working With Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box mb={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search working with..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    label="Filter by Category"
                    onChange={(e) => handleCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {categories.map(category => (
                      <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog('working')}
                  sx={{
                    background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
                  }}
                >
                  Add Working With
                </Button>
              </Grid>
            </Grid>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Category</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Display Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workingWith
                  .filter(w => w.name.toLowerCase().includes(searchTerm.toLowerCase()))
                  .map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Chip 
                          label={getCategoryName(item.category_id)} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="500">{item.name}</Typography>
                      </TableCell>
                      <TableCell>{item.description || '-'}</TableCell>
                      <TableCell>{item.display_order}</TableCell>
                      <TableCell>
                        <Chip 
                          label={item.is_active ? 'Active' : 'Inactive'}
                          color={item.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" onClick={() => handleOpenDialog('working', item)} color="primary">
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete('working', item.id, item.name)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {workingWith.length === 0 && !loading && (
            <Box textAlign="center" py={5}>
              <Typography color="textSecondary">No working with entries found. Click "Add Working With" to create one.</Typography>
            </Box>
          )}
        </TabPanel>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)', 
          color: '#fff'
        }}>
          {editingItem ? 'Edit' : 'Add'} {currentType === 'category' ? 'Category' : 'Working With'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {currentType === 'working' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category_id}
                    label="Category"
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                    required
                  >
                    {categories.map(category => (
                      <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder={currentType === 'category' ? 
                  "e.g., Government, Private, Business, Self Employed" : 
                  "e.g., Central Government, MNC, Startup, Own Business"}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography gutterBottom>Display Order</Typography>
              <Slider
                value={formData.display_order}
                onChange={(e, newValue) => setFormData({ ...formData, display_order: newValue })}
                min={0}
                max={100}
                valueLabelDisplay="auto"
                marks={[
                  { value: 0, label: '0' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50' },
                  { value: 75, label: '75' },
                  { value: 100, label: '100' }
                ]}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active === 1 || formData.is_active === true}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
            }}
          >
            {loading ? <CircularProgress size={24} /> : (editingItem ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkingWithManager;